import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Avatar,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  EmojiEvents,
  Group,
  Lightbulb,
  TrendingUp,
  Code,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const teamMembers = [
  {
    id: 1,
    name: 'Rjaa Shakoor',
    role: 'CEO & Co-Founder',
    image: '/Pictures/Rjaa Shakoor.png',
    bio: 'Visionary leader with passion for game and app development. Leading InnoVate Games & Apps to create engaging mobile experiences.',
  },
  {
    id: 2,
    name: 'Mazhar Iqbal',
    role: 'Co-Founder',
    image: '/Pictures/Male Avatar.png',
    bio: 'Strategic thinker and problem solver. Contributing to product development and business growth.',
  },
  {
    id: 3,
    name: 'Hamza Ali',
    role: 'CMO & Co-Founder',
    image: '/Pictures/Male Avatar.png',
    bio: 'Marketing strategist driving growth and brand awareness. Expert in digital marketing and user acquisition.',
  },
  {
    id: 4,
    name: 'Husnain Ameer',
    role: 'CTO & Co-Founder',
    image: '/Pictures/Male Avatar.png',
    bio: 'Technical mastermind behind our games and apps. Expert in Unity development and mobile app optimization.',
  },
  {
    id: 5,
    name: 'Hamd Iqbal',
    role: 'Team Member',
    image: '/Pictures/Male Avatar.png',
    bio: 'Contributing across product and operations with a focus on execution, quality, and team collaboration.',
  },
  {
    id: 6,
    name: 'Muhammad Shazal',
    role: 'Team Member',
    image: '/Pictures/Male Avatar.png',
    bio: 'Supporting development and growth initiatives to help deliver reliable, user-focused digital products.',
  },
];

const expertise = [
  { skill: 'Unity Game Development', level: 90 },
  { skill: 'C# Programming', level: 88 },
  { skill: 'Flutter Development', level: 85 },
  { skill: 'Full-Stack Development', level: 82 },
  { skill: 'UX/UI Design', level: 80 },
  { skill: 'AI/ML Integration', level: 75 },
];

const About = () => {
  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            About InnoVate Games & Apps
          </Typography>
          <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 8 }}>
            A Passionate Team Building Mobile Gaming & App Experiences
          </Typography>

          {/* Company Stats */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3, mb: 10 }}>
            {[
              { icon: <Code sx={{ fontSize: 40 }} />, value: '1', label: 'App Released' },
              { icon: <Group sx={{ fontSize: 40 }} />, value: '6', label: 'Team Members' },
              { icon: <EmojiEvents sx={{ fontSize: 40 }} />, value: '2025', label: 'Founded' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card sx={{ textAlign: 'center', bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ color: 'primary.main', mb: 1 }}>{stat.icon}</Box>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>

          {/* Our Story */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              mb: 10,
              backgroundColor: 'background.paper',
              borderRadius: 3,
            }}
          >
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Founded in 2025, InnoVate Games & Apps started in Bahawalpur, Pakistan, with a passionate team of four 
              co-founders united by a shared vision: to create exciting mobile games and apps that bring joy to users 
              around the world. What began as a dream has become a real startup dedicated to crafting engaging 
              digital experiences.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Our team of 6 dedicated professionals combines technical expertise with creative vision. Using 
              Unity for game development and Flutter for mobile applications, we're building games and apps that focus 
              on fun, quality, and user satisfaction. From monster truck racing to utility apps, each 
              project reflects our commitment to excellence.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Today, with our first game launched on Google Play and exciting projects in development, we're 
              just getting started. Our mission is simple: create games and apps that users love, grow our skills, 
              and establish InnoVate Games & Apps as a trusted name in mobile gaming and app development.
            </Typography>
          </Paper>

          {/* Development Process */}
          <Box sx={{ mb: 10 }}>
            <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Our Development Process
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
              From concept to launch, we follow a proven methodology
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 4 }}>
              {[
                {
                  icon: <Lightbulb sx={{ fontSize: 48 }} />,
                  title: '1. Concept & Planning',
                  description: 'Brainstorm ideas, research market trends, and create detailed game design documents.',
                },
                {
                  icon: <Code sx={{ fontSize: 48 }} />,
                  title: '2. Development',
                  description: 'Build the game using Unity with careful attention to performance and user experience.',
                },
                {
                  icon: <TrendingUp sx={{ fontSize: 48 }} />,
                  title: '3. Testing & Polish',
                  description: 'Rigorous testing, bug fixes, and optimization to ensure smooth gameplay.',
                },
                {
                  icon: <EmojiEvents sx={{ fontSize: 48 }} />,
                  title: '4. Launch & Support',
                  description: 'Release on app stores, gather feedback, and provide ongoing updates and support.',
                },
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                  >
                    <Paper
                      sx={{
                        p: 3,
                        height: '100%',
                        textAlign: 'center',
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                      }}
                    >
                      <Box sx={{ color: 'primary.main', mb: 2 }}>{step.icon}</Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Paper>
                  </motion.div>
              ))}
            </Box>
          </Box>

          {/* Expertise */}
          <Box sx={{ mb: 10 }}>
            <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
              Our Expertise
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              {expertise.map((item, index) => (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {item.skill}
                        </Typography>
                        <Typography variant="subtitle1" color="primary">
                          {item.level}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={item.level}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(99,102,241,0.15)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: 'linear-gradient(135deg, #E5E7EB 0%, #9CA3AF 100%)',
                          },
                        }}
                      />
                    </Box>
                  </motion.div>
              ))}
            </Box>
          </Box>

          {/* Team Section */}
          <Typography variant="h3" gutterBottom align="center" sx={{ mb: 6, fontWeight: 'bold' }}>
            Leadership Team
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 4, mb: 10 }}>
            {teamMembers.map((member, index) => (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      bgcolor: 'background.paper',
                      borderRadius: 3,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(99,102,241,0.25)',
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Avatar
                        src={member.image}
                        alt={member.name}
                        sx={{
                          width: 120,
                          height: 120,
                          margin: '0 auto 16px',
                          border: 3,
                          borderColor: 'primary.main',
                        }}
                      />
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {member.name}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        gutterBottom
                        sx={{ mb: 2 }}
                      >
                        {member.role}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        {member.bio}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
            ))}
          </Box>

          {/* Core Values */}
          <Box sx={{ mb: 10, bgcolor: 'background.paper', p: { xs: 3, md: 6 }, borderRadius: 3 }}>
            <Typography variant="h3" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 6 }}>
              Our Core Values
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
              {[
                {
                  title: 'Move Fast',
                  description: 'Ship MVPs in weeks, not months. Iterate based on real user data, not assumptions.',
                },
                {
                  title: 'Think Big',
                  description: 'We are not building games - we are building the future of entertainment and the metaverse.',
                },
                {
                  title: 'Data-Driven',
                  description: 'Every decision backed by metrics. A/B test everything. Optimize relentlessly.',
                },
                {
                  title: 'User-Obsessed',
                  description: 'Talk to users daily. Their feedback shapes our roadmap. Build what they actually want.',
                },
                {
                  title: 'Open Source',
                  description: 'We contribute back to the community. Transparency and collaboration make us stronger.',
                },
                {
                  title: 'Remote-First',
                  description: 'Talent is everywhere. We hire the best regardless of location. Async by default.',
                },
              ].map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {value.description}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Box>

          {/* Call to Action */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, #E5E7EB 0%, #9CA3AF 100%)',
              borderRadius: 4,
            }}
          >
            <Typography variant="h3" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
              We're Hiring!
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'white', opacity: 0.95 }}>
              Join a fast-growing startup backed by top VCs. Competitive salary, equity, and remote-first culture.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="mailto:careers@innovategames.net"
              sx={{
                background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                color: '#FFFFFF',
                borderRadius: '14px',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 28px rgba(31,41,55,0.35)',
                },
                py: 2,
                px: 5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                boxShadow: '0 8px 24px rgba(31,41,55,0.25)',
                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              }}
            >
              View Career Opportunities
            </Button>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About; 
